import { HttpContext } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Address, BundleEntry, ContactPoint, Patient } from 'fhir/r4';
import { authBackendEndPointToken, backendEndPointToken } from '../app.config';
import { DisableCountParam, LoadingUIEnabled } from '../loading.interceptor';
import { TableFilters, TableFilteringService, TableRowModel } from '../shared/table-filtering.service';

export interface PatientRow extends TableRowModel {
    rawResource: Patient;
    patient: Patient;
    name?: Patient['name'];
    telecom?: Patient['telecom'];
    address?: Patient['address'];
    gender?: Patient['gender'];
    birthDate?: Patient['birthDate'];
    identifier?: Patient['identifier'];
    primaryIdentifier?: string | null;
    primaryPhone?: string | null;
    primaryEmail?: string | null;
    addressLabel?: string | null;
}

export interface PatientSearchInitOptions {
    excludePharmacyWalkIns?: boolean;
    pharmacyWalkInTagQuery?: string;
    pageSize?: number;
    maxCacheRows?: number;
}

@Injectable({ providedIn: 'root' })
export class PatientSearchService implements OnDestroy {
    private readonly tableFiltering = inject<TableFilteringService<Patient, PatientRow>>(TableFilteringService);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly silentHttpContext = new HttpContext()
        .set(LoadingUIEnabled, false)
        .set(DisableCountParam, true);

    private readonly internalDataSource = new MatTableDataSource<PatientRow>();

    private excludeWalkIns = true;
    private pharmacyTagQuery: string | undefined;
    private pageSize = 25;
    private maxCacheRows = 200;

    readonly loading$ = this.tableFiltering.loading$;
    readonly total$ = this.tableFiltering.total$;
    readonly displayedTotal$ = this.tableFiltering.displayedTotal$;
    readonly unknownTotal$ = this.tableFiltering.unknownTotal$;
    readonly error$ = this.tableFiltering.error$;

    init(options: PatientSearchInitOptions = {}): void {
        this.excludeWalkIns = options.excludePharmacyWalkIns ?? true;
        this.pharmacyTagQuery = options.pharmacyWalkInTagQuery;
        this.pageSize = options.pageSize ?? this.pageSize;
        this.maxCacheRows = options.maxCacheRows ?? this.maxCacheRows;

        this.tableFiltering.init({
            resourceType: 'Patient',
            defaultQuery: this.buildDefaultQuery(),
            dataSource: this.internalDataSource,
            baseUrl: this.backendUrl,
            pageSize: this.pageSize,
            maxCacheRows: this.maxCacheRows,
            transformEntry: (resource, entry) => this.transformPatient(resource, entry),
            httpOptions: {
                context: this.silentHttpContext
            }
        });
    }

    get dataSource(): MatTableDataSource<PatientRow> {
        return this.internalDataSource;
    }

    search(filters: TableFilters): void {
        this.tableFiltering.search(filters);
    }

    changePage(pageIndex: number): void {
        this.tableFiltering.changePage(pageIndex);
    }

    refresh(): void {
        this.tableFiltering.refreshCurrentPage();
    }

    destroy(): void {
        this.tableFiltering.destroy();
        this.internalDataSource.disconnect();
        this.internalDataSource.data = [];
    }

    ngOnDestroy(): void {
        this.destroy();
    }

    private buildDefaultQuery(): string {
        const params = new URLSearchParams();
        params.set('active', 'true');
        params.set('deceased:not', 'true');
        params.set('_count', String(this.pageSize));

        if (this.excludeWalkIns && this.pharmacyTagQuery) {
            params.set('_tag:not', this.pharmacyTagQuery);
        }

        return `/Patient?${params.toString()}`;
    }

    private transformPatient(resource: Patient, entry?: BundleEntry<Patient>): PatientRow {
        const fallbackId = resource.id ?? entry?.resource?.id ?? entry?.fullUrl ?? `Patient-${Date.now()}`;
        const primaryIdentifier = resource.identifier?.find((identifier) => !!identifier?.value)?.value ?? null;

        return {
            id: fallbackId,
            resourceType: resource.resourceType ?? 'Patient',
            display: this.buildName(resource) ?? fallbackId,
            rawResource: resource,
            patient: resource,
            name: resource.name,
            telecom: resource.telecom,
            address: resource.address,
            gender: resource.gender,
            birthDate: resource.birthDate,
            identifier: resource.identifier,
            primaryIdentifier,
            primaryPhone: this.extractContact(resource.telecom, 'phone'),
            primaryEmail: this.extractContact(resource.telecom, 'email'),
            addressLabel: this.buildAddress(resource.address)
        };
    }

    private buildName(resource: Patient): string | null {
        const preferredName = resource.name?.find((name) => name.use === 'official' || name.use === 'usual') ?? resource.name?.[0];
        if (!preferredName) {
            return null;
        }
        const given = preferredName.given?.join(' ') ?? '';
        const family = preferredName.family ?? '';
        return [given, family].filter(Boolean).join(' ').trim() || null;
    }

    private extractContact(telecom: ContactPoint[] | undefined, system: string): string | null {
        if (!telecom?.length) {
            return null;
        }
        return telecom.find((contact) => contact.system === system)?.value ?? null;
    }

    private buildAddress(addresses: Address[] | undefined): string | null {
        if (!addresses?.length) {
            return null;
        }
        const preferred = addresses.find((addr) => addr.use === 'home') ?? addresses[0];
        const parts = [preferred.line?.join(' '), preferred.city, preferred.state, preferred.country];
        const formatted = parts.filter(Boolean).join(', ').trim();
        return formatted || null;
    }
}
